/* eslint-disable complexity */
import Button from 'carbon-react/lib/components/button';
import Box from 'carbon-react/lib/components/box';
import { capitalize } from 'lodash';
import { useSelector } from 'react-redux';
import React, { useCallback } from 'react';

import { Intent, SignupEstimate, TermEstimate } from '@futrli-sdk/billing';
import { Organisation } from '@futrli-sdk/organisations';
import { Workspace, WorkspacesListingItem } from '@futrli-sdk/workspaces';
import { AnyError, ERROR_STATUS, getErrorStatus } from '@futrli/errors';
import { FormattedMessage, injectIntl, WrappedComponentProps } from '@futrli/localisation';
import { ProductSlug, workspaceByIdSelector } from '@futrli/shared-services';

import { PaymentForm } from '../../../billing-forms';
import { CouponFormField } from '../../../billing-forms/components/coupon-field/coupon-form-field';
import { paymentDetailsFormConfig } from '../../../billing-forms/config/payment-details-form-config';
import { Callback, PaymentFormData } from '../../../billing-forms/types';
import {
  activateSubscription,
  createCustomer,
  createPaymentIntent,
  createSetupIntent,
  fetchSignupEstimates,
  handlePaymentIntent,
  handleSetupIntent
} from '../../../core/api';
import { UIElements } from '../../../core/components/stripe-styled-components';
import { TRACKING_EVENTS } from '../../../core/constants';
import { trackCTA, trackEvent, trackPaymentError, trackPaymentSuccess, trackViewModal } from '../../../core/tracker';
import { BillingProduct, BillingTerm } from '../../../core/types';
import { getCreateCustomerPayload } from '../../../utils/api-formatters';
import { getFormError } from '../../../utils/error-utils';
import { ProductPlanSelection } from './product-plan-selection';
import { StoreState } from '../../../core/types';
import { ActivationEvent } from './activate-product-sidebar';

interface OwnProps {
  workspaceId: string;
  isLoading: boolean;
  organisation?: Organisation;
  product: BillingProduct;
  onLoading: (isLoading: boolean) => void;
  onComplete: () => void;
  onClose: (event: ActivationEvent) => void;
  onError: (dispatch: React.Dispatch<AnyError | null>) => void;
  isOpen: boolean;
  defaultMessage?: string;
  handleChangeSubscribeDialogTitle: (newTitle: string) => void;
}
type Props = OwnProps & WrappedComponentProps;

interface FormState {
  data: PaymentFormData | undefined;
  isValid: boolean;
  isSubmitting: boolean;
  error: AnyError | null;
}

export interface CouponState {
  value: string;
  termEstimate: TermEstimate;
}

/**
 * Renders the dialog content with the different subscription options,
 * collects the payment details and activates the product.
 * @param {Props} props
 * @returns {JSX.Element}
 */
export const SubscribeAndPurchaseDialogContentFC: React.FC<Props> = (props: Props) => {
  const {
    isLoading,
    onClose,
    onError,
    onLoading,
    product,
    workspaceId,
    isOpen,
    intl,
    handleChangeSubscribeDialogTitle
  } = props;
  const productSlug = product.slug as ProductSlug;
  const [signupEstimates, setSignupEstimates] = React.useState<SignupEstimate | null>(null);
  const [selectedPlanTerm, setSelectedPlanTerm] = React.useState<BillingTerm | null>(null);
  const [couponState, setCouponState] = React.useState<CouponState | null>(null);
  const [isCouponBeingChecked, setIsCouponBeingChecked] = React.useState<boolean>(false);
  const [formState, setFormState] = React.useState<FormState>({
    data: undefined,
    isValid: false,
    isSubmitting: false,
    error: null
  });

  const setIsSubmitting = useCallback((isSubmitting: boolean) => {
    setFormState(prevState => ({ ...prevState, isSubmitting }));
  }, []);

  const workspace = useSelector((store: StoreState) => {
    const { normalised } = store;
    return workspaceByIdSelector(normalised, workspaceId);
  });

  const handleOnCheckingCoupon = (isBeingChecked: boolean) => {
    setIsCouponBeingChecked(isBeingChecked);
  };
  const handleOnRemoveCoupon = () => {
    setCouponState(null);
  };
  const handleOnApplyCoupon = (value: string, termEstimate: TermEstimate) => {
    setCouponState({ value, termEstimate });
  };
  const handleOnSelectPlan = (term: BillingTerm) => {
    trackCTA(`${capitalize(term)} plan`, 'Go Pro');
    setSelectedPlanTerm(term);
    handleChangeSubscribeDialogTitle(
      intl.formatMessage({ id: 'billing.payment_details', defaultMessage: 'Payment details' })
    );
  };
  const handleOnValidateForm = (isValid: boolean) => {
    setFormState(prevState => ({ ...prevState, isValid }));
  };

  React.useEffect(() => {
    const loadData = async () => {
      try {
        const fetchedSignupEstimates = await fetchSignupEstimates(workspaceId);
        setSignupEstimates(fetchedSignupEstimates);
        onLoading(false);
      } catch (error) {
        onError(error as never);
      }
    };

    if (isOpen) {
      loadData();
    }
  }, [onError, onLoading, product.slug, workspaceId, isOpen]);

  // analytics tracking for different states
  React.useEffect(() => {
    trackModalState(signupEstimates, productSlug, selectedPlanTerm);
  }, [selectedPlanTerm, signupEstimates, productSlug]);

  if (isLoading || !signupEstimates) {
    return <React.Fragment />;
  }

  return (
    <div style={{ height: '100%' }}>
      {signupEstimates && !selectedPlanTerm && (
        <React.Fragment>
          <ProductPlanSelection signupEstimates={signupEstimates} onSelectPlan={handleOnSelectPlan} />
        </React.Fragment>
      )}
      {selectedPlanTerm && (
        <UIElements>
          <PaymentForm
            setIsSubmitting={setIsSubmitting}
            externalError={formState.error}
            onPreSubmit={handleOnPreSubmit(props, signupEstimates, setFormState, couponState)}
            onSubmit={handleSubmitForm(props, signupEstimates, couponState, setFormState, workspace)}
            isSubmitting={formState.isSubmitting}
            onValidate={handleOnValidateForm}
            onStripeError={handleOnStripeFormError(setFormState)}
            formConfig={paymentDetailsFormConfig}
            usePaymentIntent={shouldUsePaymentIntentFlow(couponState, signupEstimates)}
            product={productSlug}
            formData={formState.data}
            formActions={
              <Box
                display="flex"
                position="absolute"
                bottom={0}
                right={0}
                padding="16px"
                backgroundColor="#fff"
                width="100%"
              >
                <Button onClick={onClose} buttonType="secondary" mr="16px" ml="auto">
                  <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
                </Button>
                <Button
                  type="submit"
                  buttonType="primary"
                  disabled={isCouponBeingChecked || !formState.isValid || formState.isSubmitting}
                >
                  <FormattedMessage id="common.lets_go" defaultMessage="Let's go!" />
                </Button>
              </Box>
            }
            formAdditionalFields={
              <CouponFormField
                workspaceId={workspaceId}
                productSlug={productSlug}
                subscriptionTerm={selectedPlanTerm}
                existingCustomer={false}
                onChecking={handleOnCheckingCoupon}
                onClean={handleOnRemoveCoupon}
                onComplete={handleOnApplyCoupon}
              />
            }
            isSubscribeAndPurchaseDialog
          />
        </UIElements>
      )}
    </div>
  );
};
export const SubscribeAndPurchaseDialogContent = injectIntl(SubscribeAndPurchaseDialogContentFC);

export function trackModalState(
  signupEstimate: SignupEstimate | null,
  productSlug: string,
  selectedPlanTerm: string | null
) {
  if (signupEstimate && !selectedPlanTerm) {
    trackViewModal(`${capitalize(productSlug)} Pro payment options`);
  }
  if (selectedPlanTerm) {
    trackViewModal('Payment details');
  }
}

export function shouldUsePaymentIntentFlow(couponState: CouponState | null, signupEstimate: SignupEstimate): boolean {
  if (!couponState?.value) {
    return signupEstimate.estimated_cost > 0;
  }

  return couponState.termEstimate.estimated_cost > 0;
}

export function handleSubmitForm(
  { intl, workspaceId, product, onError, onComplete }: Props,
  signupEstimate: SignupEstimate,
  coupon: CouponState | null,
  dispatch: React.Dispatch<React.SetStateAction<FormState>>,
  workspace: Workspace | WorkspacesListingItem | null
) {
  return async (formData: PaymentFormData) => {
    try {
      dispatch(prevState => ({ ...prevState, isSubmitting: true }));
      let subscription;

      if (workspace && workspace.subscription_state) {
        subscription = await activateSubscription(workspaceId, {
          intent_id: formData.paymentMethodId,
          coupon_ids: coupon?.value ? [coupon.value] : []
        });
      }

      if (subscription) {
        trackPaymentSuccess(
          product,
          intl,
          subscription,
          coupon?.termEstimate.estimated_cost || signupEstimate.estimated_cost
        );
      }

      trackEvent(TRACKING_EVENTS.PURCHASED_SUBSCRIPTION_DIRECT);

      dispatch(prevState => ({ ...prevState, isSubmitting: false }));
      onComplete();
    } catch (error) {
      handleErrorResponse(error, product, onError, dispatch, !shouldUsePaymentIntentFlow(coupon, signupEstimate));
    }
  };
}

export function handleOnPreSubmit(
  props: Props,
  signupEstimate: SignupEstimate,
  dispatch: React.Dispatch<React.SetStateAction<FormState>>,
  couponState: CouponState | null
) {
  return async (formData: PaymentFormData) => {
    const isPaymentIntentFlow = shouldUsePaymentIntentFlow(couponState, signupEstimate);

    try {
      dispatch(prevState => ({ ...prevState, isSubmitting: true, error: null }));
      await createCustomer(props.workspaceId, getCreateCustomerPayload(formData));

      return isPaymentIntentFlow
        ? managePaymentIntentActions(props, dispatch, couponState?.value)
        : manageSetupIntentActions(props, dispatch);
    } catch (error) {
      handleErrorResponse(error, props.product, props.onError, dispatch, !isPaymentIntentFlow);
    }
  };
}

export function handleOnStripeFormError(dispatch: React.Dispatch<React.SetStateAction<FormState>>) {
  return () => {
    dispatch(prevState => ({ ...prevState, isSubmitting: false }));
  };
}

export function managePaymentIntentActions(
  { workspaceId, product, onError }: Props,
  dispatch: React.Dispatch<React.SetStateAction<FormState>>,
  coupon?: string
) {
  return {
    createIntent: async (paymentMethodId?: string): Promise<Intent> => {
      const productEstimate = await fetchSignupEstimates(workspaceId, {
        coupon_ids: coupon || ''
      });

      return withManageErrors(
        () =>
          createPaymentIntent(workspaceId, {
            amount: productEstimate.estimated_cost,
            currency_code: productEstimate.currency_code,
            payment_method_id: paymentMethodId
          }),
        product,
        onError,
        dispatch
      );
    },
    handleIntent: (intentId: string): Promise<Intent> =>
      withManageErrors(() => handlePaymentIntent(workspaceId, intentId), product, onError, dispatch)
  };
}

export function manageSetupIntentActions(
  { product, workspaceId, onError }: Props,
  dispatch: React.Dispatch<React.SetStateAction<FormState>>
) {
  return {
    createIntent: (): Promise<Intent> => {
      return withManageErrors(() => createSetupIntent(workspaceId), product, onError, dispatch);
    },
    handleIntent: (intentId: string): Promise<Intent> => {
      return withManageErrors(() => handleSetupIntent(workspaceId, intentId), product, onError, dispatch);
    }
  };
}

export function handleErrorResponse(
  error: AnyError,
  product: BillingProduct,
  onError: Props['onError'],
  dispatch: React.Dispatch<React.SetStateAction<FormState>>,
  isSetupIntentFlow?: boolean
) {
  if (getErrorStatus(error) !== ERROR_STATUS._400) {
    onError(error);
    return;
  }

  trackPaymentError(product.name, error, isSetupIntentFlow);
  dispatch(prevState => ({
    ...prevState,
    error: getFormError(error),
    isSubmitting: false
  }));
}

export async function withManageErrors(
  asyncAction: Callback,
  product: BillingProduct,
  onError: Props['onError'],
  dispatch: React.Dispatch<React.SetStateAction<FormState>>,
  isSetupIntentFlow?: boolean
) {
  try {
    return await asyncAction();
  } catch (error) {
    handleErrorResponse(error, product, onError, dispatch, isSetupIntentFlow);
  }
}
